import type { QueryClient } from "@tanstack/vue-query";
import type { WorkspaceActivityFeedFilter } from "../activity-feed/useWorkspaceActivityFeedFilter";
import type { WorkspaceFilter, WorkspaceViewFilterType } from "../workspace-table/workspaceViewFilter";
import { infiniteQueryOptions, queryOptions, skipToken } from "@tanstack/vue-query";
import { getItemsForFilesystem } from "ls/api/cloud/feed";
import { getFilesystemNodeContents, getFilesystemRootFolderContents, getFolderNavById, getSuggestedWorkspaces, getUserInvitations, getUserOrganizationWorkspaces, getUserWorkspaces, getUserWorkspacesNotificationState, getWorkspace, getWorkspaceFile, getWorkspaceSecurity } from "ls/api/cloud/fs";
import { STALE } from "ls/queries/staleTime";
import { jsDateFromAspDateString } from "@/common/lib";
import { queries as fileQueries } from "../files/queries";

export interface FetchContentsErrorCause {
  id: string;
  type: "workspace" | "folder";
}

export type FetchContentError = Error & {
  cause: FetchContentsErrorCause;
};

export function isFetchContentsError(error: Error): error is Error & { cause: FetchContentsErrorCause } {
  if (!error.cause) return false;
  if (typeof error.cause !== "object") return false;
  return "id" in error.cause && "type" in error.cause;
}

export const queries = {
  workspaces: () => ["workspaces"] as const,
  workspacesNotificationState: () =>
    queryOptions({
      queryKey: [...queries.workspaces(), "notificationState"] as const,
      queryFn: ({ signal }) => getUserWorkspacesNotificationState({ signal }),
    }),
  workspace: (workspaceId: string) => [...queries.workspaces(), workspaceId] as const,
  list: () =>
    queryOptions({
      queryKey: [...queries.workspaces(), "list"] as const,
      queryFn: ({ signal }) => getUserWorkspaces({ signal }),
    }),
  listOrganization: (organizationId: string) =>
    queryOptions({
      queryKey: [...queries.workspaces(), "list", { organizationId }] as const,
      queryFn: ({ signal }) => getUserOrganizationWorkspaces({ organizationId }, { signal }),
    }),
  workspaceContentsKey: (workspaceId: string) => [...queries.workspaces(), workspaceId, "contents"] as const,
  contents: (workspaceId: string, options: WorkspaceFilter, queryClient?: QueryClient) => {
    const { workspaceViewFilter = "all", assigneeFilter } = options;
    return queryOptions({
      queryKey: [...queries.workspaceContentsKey(workspaceId), { workspaceViewFilter, assigneeFilter }] as const,
      queryFn: async ({ signal }) => {
        try {
          const contents = await getFilesystemRootFolderContents({
            workspaceId,
            workspaceViewFilter,
            assigneeFilter,
          }, { signal });

          // pre-populate data for project info query
          if (queryClient) {
            for (const shortcutJs of contents.Listing.Shortcuts) {
              queryClient.setQueryData(fileQueries.info(shortcutJs.Shortcut).queryKey, shortcutJs);
            }
          }

          return contents;
        } catch (error) {
          if (error instanceof Error) {
            const cause: FetchContentsErrorCause = {
              id: workspaceId,
              type: "workspace",
            };
            error.cause = cause;
          }
          throw error;
        }
      },
    });
  },
  info: (workspaceId: string | undefined, queryClient?: QueryClient) =>
    queryOptions({
      queryKey: [...queries.workspace(workspaceId ?? ""), "info"] as const,
      queryFn: workspaceId
        ? async ({ signal }) => {
          try {
            return await getWorkspace({ workspaceId }, { signal });
          } catch (error) {
            if (error instanceof Error) {
              const cause: FetchContentsErrorCause = {
                id: workspaceId,
                type: "workspace",
              };
              error.cause = cause;
            }
            throw error;
          }
        }
        : skipToken,
      placeholderData: () => {
        if (!queryClient) return;
        const queryKey = queries.list().queryKey;
        return queryClient.getQueryData(queryKey)?.Filesystems.find(d => d.FilesystemId === workspaceId);
      },
      staleTime: STALE.SECONDS.FIFTEEN,
    }),
  workspaceFile: (workspaceId: string) =>
    queryOptions({
      queryKey: [...queries.workspace(workspaceId), "file"] as const,
      queryFn: ({ signal }) => getWorkspaceFile({ workspaceId }, { signal }),
      staleTime: STALE.INFINITY,
    }),
  activity: (workspaceId: string, filter?: WorkspaceActivityFeedFilter) =>
    infiniteQueryOptions({
      queryKey: [...queries.workspace(workspaceId), "activity", filter] as const,
      queryFn: ({ pageParam, signal }) => {
        const untilTime = pageParam ? jsDateFromAspDateString(pageParam) : undefined;
        return getItemsForFilesystem(workspaceId, {
          untilTime,
          highlightsOnly: filter?.highlightsOnly,
        }, { signal });
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: lastPage => {
        // server doesn't return total or if there's next page available
        // so if total returned items is less than single page size then it was the last page
        if (lastPage.FeedItems.length < 25) {
          return undefined;
        }
        return lastPage.FeedItems.at(-1)?.Timestamp;
      },
    }),
  security: (workspaceId: string) =>
    queryOptions({
      queryKey: [...queries.workspace(workspaceId), "security"] as const,
      queryFn: ({ signal }) => getWorkspaceSecurity({ workspaceId }, { signal }),
    }),
  folders: () => ["folders"] as const,
  folder: (folderId: string) => [...queries.folders(), folderId] as const,
  folderLocation: (folderId: string) =>
    queryOptions({
      queryKey: [...queries.folder(folderId), "location"] as const,
      queryFn: ({ signal }) => {
        return getFolderNavById(folderId, { signal });
      },
      staleTime: STALE.SECONDS.FIFTEEN,
    }),
  folderContentsKey: (id: string) => [...queries.folder(id), "contents"] as const,
  folderContents: (id: string, queryClient: QueryClient, options: { workspaceViewFilter?: WorkspaceViewFilterType; assigneeFilter?: string[] }) => {
    const { workspaceViewFilter = "all", assigneeFilter } = options;
    return queryOptions({
      queryKey: [...queries.folderContentsKey(id), { workspaceViewFilter, assigneeFilter }] as const,
      queryFn: async ({ signal }) => {
        try {
          const contents = await getFilesystemNodeContents(id, { workspaceViewFilter, assigneeFilter }, { signal });

          // pre-populate data for project info query
          for (const shortcutJs of contents.Shortcuts) {
            queryClient.setQueryData(fileQueries.info(shortcutJs.Shortcut).queryKey, shortcutJs);
          }

          return contents;
        } catch (error) {
          if (error instanceof Error) {
            const cause: FetchContentsErrorCause = {
              id,
              type: "folder",
            };
            error.cause = cause;
          }
          throw error;
        }
      },
    });
  },
  invitations: () => ["invitations"] as const,
  openInvitations: () =>
    queryOptions({
      queryKey: [...queries.invitations(), "list"] as const,
      queryFn: ({ signal }) => getUserInvitations({ signal }),
      staleTime: STALE.SECONDS.FIFTEEN,
    }),
  suggestedWorkspaces: (organizationId: string) =>
    queryOptions({
      queryKey: ["workspaces", "suggested", { organizationId }] as const,
      queryFn: ({ signal }) => getSuggestedWorkspaces({ organizationId }, { signal }),
    }),
};
